import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Swords, Trophy, ArrowLeft, Sparkles, Loader2, Eye, Heart, Play } from "lucide-react";
import { toast } from "sonner";

export default function Battle() {
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [winnerId, setWinnerId] = useState(null);
  const [score, setScore] = useState({ wins: 0, total: 0 });

  const loadPair = useCallback(() => {
    return api
      .get("/battle/pair")
      .then(r => { setPair(r.data); setWinnerId(null); })
      .catch(() => toast.error("Pas assez de vidéos pour un battle"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPair(); }, [loadPair]);

  const nextBattle = () => { setLoading(true); loadPair(); };

  const vote = async (winner) => {
    if (voting || winnerId) return;
    setVoting(true);
    setWinnerId(winner.id);
    try {
      await api.post("/battle/vote", null, { params: { winner_id: winner.id } });
      setScore(s => ({ wins: s.wins + 1, total: s.total + 1 }));
      // Brief reveal animation, then load next pair
      setTimeout(nextBattle, 1300);
    } catch {
      toast.error("Vote échoué");
      setWinnerId(null);
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="battle-page">
      <div className="mb-8">
        <Link to="/trending" className="inline-block mb-4 text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] hover:text-[color:var(--accent)]">
          <ArrowLeft className="w-3 h-3 inline mr-1" /> Retour aux tendances
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <Swords className="w-7 h-7 text-[color:var(--accent)]" />
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)]">Vote · Mode duel</div>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-display text-5xl sm:text-6xl leading-none">Battle</h1>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] text-xs uppercase tracking-widest text-[color:var(--text-muted)]" data-testid="battle-score">
            <Trophy className="w-3.5 h-3.5 text-[color:var(--accent)]" />
            <span className="text-white font-mono">{score.total}</span> votes
          </div>
        </div>
        <p className="text-[color:var(--text-muted)] mt-4 max-w-2xl">
          Deux vidéos face à face. Vote pour celle que tu préfères — c'est ton oreille qui décide.
        </p>
      </div>

      {loading && (
        <div className="py-32 text-center text-[color:var(--text-muted)]">
          <Loader2 className="w-8 h-8 mx-auto animate-spin mb-3" />
          Chargement du duel...
        </div>
      )}

      {!loading && pair && (
        <div className="relative">
          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch" data-testid="battle-arena">
            <BattleCard
              video={pair.a}
              side="left"
              onVote={() => vote(pair.a)}
              isWinner={winnerId === pair.a.id}
              isLoser={winnerId && winnerId !== pair.a.id}
              disabled={voting || !!winnerId}
              testid="battle-card-a"
            />
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                <div className="font-display text-7xl text-[color:var(--accent)] opacity-90">VS</div>
                <Sparkles className="absolute -top-3 -right-6 w-6 h-6 text-[color:var(--accent)] animate-pulse" />
              </div>
            </div>
            <div className="flex lg:hidden items-center justify-center my-2">
              <div className="font-display text-5xl text-[color:var(--accent)]">VS</div>
            </div>
            <BattleCard
              video={pair.b}
              side="right"
              onVote={() => vote(pair.b)}
              isWinner={winnerId === pair.b.id}
              isLoser={winnerId && winnerId !== pair.b.id}
              disabled={voting || !!winnerId}
              testid="battle-card-b"
            />
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={nextBattle}
              disabled={voting}
              data-testid="battle-skip"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] text-xs uppercase tracking-widest transition disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" /> Nouveau duel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BattleCard({ video, side, onVote, isWinner, isLoser, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onVote}
      disabled={disabled}
      data-testid={testid}
      className={`group relative rounded-2xl overflow-hidden border-2 bg-[color:var(--bg-elev)] text-left transition-all duration-500 ${
        isWinner
          ? "border-[color:var(--accent)] ring-4 ring-[color:var(--accent)]/40 scale-105 shadow-2xl"
          : isLoser
          ? "opacity-30 grayscale border-[color:var(--border)] scale-95"
          : "border-[color:var(--border)] hover:border-[color:var(--accent)] hover:scale-[1.02] cursor-pointer"
      }`}
      aria-label={`Voter pour ${video.title}`}
    >
      <div className="relative aspect-video bg-black overflow-hidden">
        {video.thumbnail && (
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        {/* Side label */}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-[10px] uppercase tracking-widest font-bold text-white">
          {side === "left" ? "A" : "B"}
        </div>
        {/* Channel pill */}
        {video.channel_name && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-[10px] uppercase tracking-widest text-white">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: video.channel_color }} />
            {video.channel_name}
          </div>
        )}
        {/* Winner overlay */}
        {isWinner && (
          <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--accent)]/20 backdrop-blur-sm">
            <div className="px-6 py-3 rounded-full bg-[color:var(--accent)] text-black font-display text-3xl flex items-center gap-2 shadow-2xl">
              <Trophy className="w-6 h-6" /> Vainqueur
            </div>
          </div>
        )}
        {/* Hover play icon */}
        {!isWinner && !isLoser && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-16 h-16 rounded-full bg-[color:var(--accent)] text-black flex items-center justify-center shadow-2xl">
              <Heart className="w-7 h-7" />
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <h2 className="font-display text-xl sm:text-2xl leading-tight mb-2 line-clamp-2">
          {video.title}
        </h2>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
          <span className="inline-flex items-center gap-1">
            <Eye className="w-3 h-3" /> {video.views || 0}
          </span>
          <span className="inline-flex items-center gap-1">
            <Heart className="w-3 h-3" /> {video.likes || 0}
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--accent)] font-semibold">
            <Play className="w-3 h-3" /> Voter
          </span>
        </div>
      </div>
    </button>
  );
}
